/**
 * CLIENTES Y CENTROS
 *
 * Lista de clientes, detalle con sus centros, y alta/edición cuando el
 * usuario tiene permiso. Los códigos son inmutables después del alta:
 * se muestran en campos deshabilitados, no para engañar sino para que
 * quede claro que no se pueden cambiar.
 */
import { useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  api,
  type Centro,
  type CentroDetalle,
  type Cliente,
  type ClienteDetalle,
  FalloApi,
} from '../api.ts';
import {
  alternarOrden,
  Avatar,
  CabeceraOrdenable,
  CampoTexto,
  DiasOperativosInput,
  Error as ErrorCaja,
  FiltrosResponsive,
  Panel,
  useDatos,
  Vacio,
} from '../componentes/base.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useLogoRemoto } from '../componentes/logo-remoto.ts';
import { useSesion } from '../contexto.tsx';
import { useListaUrl } from '../listaUrl.ts';

/**
 * Un componente propio (no un `useLogoRemoto` suelto dentro del `.map`
 * de la fila) porque un hook no puede llamarse dentro de un bucle cuyo
 * número de iteraciones cambia entre renders — el filtro de la lista lo
 * cambia constantemente.
 */
function AvatarCliente({ cliente, pequeno }: { cliente: Cliente; pequeno?: boolean }): ReactNode {
  const logoUrl = useLogoRemoto(cliente.id, cliente.tieneLogo);
  return <Avatar nombre={cliente.razonSocial} logoUrl={logoUrl} pequeno={pequeno} />;
}

// ----------------------------------------------------------------- clientes

type EstadoFiltro = 'TODOS' | 'ACTIVOS' | 'INACTIVOS';
type OrdenClientes = 'razonSocial' | 'codigo' | 'estado';
type Dir = 'asc' | 'desc';

/**
 * `estado: 'TODOS'` porque hoy —sin esta fase— la lista ya enseña activos e
 * inactivos juntos (no hay filtro): excluir inactivos por defecto sería un
 * cambio de comportamiento, no una migración a URL.
 */
const DEFAULTS_CLIENTES = { q: '', estado: 'TODOS', orden: 'razonSocial', dir: 'asc' } as const;

export function PaginaClientes(): ReactNode {
  const { puede } = useSesion();
  const navegar = useNavigate();
  const estado = useDatos(() => api.get<{ clientes: Cliente[] }>('/api/clientes'), []);
  const [mostrarNuevo, setMostrarNuevo] = useState(false);
  const lista = useListaUrl(DEFAULTS_CLIENTES);

  const q = lista.valor('q');
  const filtroEstado = lista.valor('estado') as EstadoFiltro;
  const orden = lista.valor('orden') as OrdenClientes;
  const dir = lista.valor('dir') as Dir;

  function ordenarPor(columna: OrdenClientes): void {
    const siguiente = alternarOrden(columna, orden, dir);
    lista.fijarVarios({ orden: siguiente.orden, dir: siguiente.dir });
  }

  //  FASE 3.3-D3: badge «Filtros (N)» — nunca `q`/`orden`/`dir`.
  const contadorFiltros = filtroEstado !== DEFAULTS_CLIENTES.estado ? 1 : 0;

  const filtrados = useMemo(() => {
    const t = q.trim().toLowerCase();
    let l = estado.datos?.clientes ?? [];
    if (filtroEstado === 'ACTIVOS') l = l.filter((c) => c.activo);
    else if (filtroEstado === 'INACTIVOS') l = l.filter((c) => !c.activo);
    if (t !== '') {
      l = l.filter((c) => c.razonSocial.toLowerCase().includes(t) || c.codigo.toLowerCase().includes(t));
    }
    const factor = dir === 'asc' ? 1 : -1;
    return [...l].sort((a, b) => {
      if (orden === 'codigo') return factor * a.codigo.localeCompare(b.codigo);
      if (orden === 'estado') return factor * (Number(a.activo) - Number(b.activo));
      return factor * a.razonSocial.localeCompare(b.razonSocial);
    });
  }, [estado.datos, q, filtroEstado, orden, dir]);

  return (
    <Shell
      titulo="Clientes"
      acciones={
        puede('clientes:crear') ? (
          <button
            className={mostrarNuevo ? '' : 'principal'}
            onClick={() => setMostrarNuevo((v) => !v)}
          >
            {mostrarNuevo ? 'Cancelar' : 'Nuevo cliente'}
          </button>
        ) : undefined
      }
    >
      <div className="panel">
        {mostrarNuevo && (
          <FormNuevoCliente
            alCrear={(id) => {
              setMostrarNuevo(false);
              estado.recargar();
              navegar(`/clientes/${id}`);
            }}
            alCancelar={() => setMostrarNuevo(false)}
          />
        )}
        <Panel estado={estado}>
          {(d) =>
            d.clientes.length === 0 ? (
              <Vacio titulo="No hay clientes a tu alcance">
                <p className="suave">
                  Si esperabas ver alguno, revisa con administración qué centros tienes asignados.
                </p>
              </Vacio>
            ) : (
              <>
                <FiltrosResponsive
                  contador={contadorFiltros}
                  busqueda={
                    <div className="campo campo-busqueda">
                      <label htmlFor="buscar-cliente">Buscar</label>
                      <input
                        id="buscar-cliente"
                        type="text"
                        placeholder="Razón social o código…"
                        value={q}
                        onChange={(e) => lista.fijar('q', e.target.value)}
                      />
                    </div>
                  }
                >
                  <div className="campo">
                    <label htmlFor="filtro-estado-cliente">Estado</label>
                    <select
                      id="filtro-estado-cliente"
                      value={filtroEstado}
                      onChange={(e) => lista.fijar('estado', e.target.value)}
                    >
                      <option value="TODOS">Todos</option>
                      <option value="ACTIVOS">Activos</option>
                      <option value="INACTIVOS">Inactivos</option>
                    </select>
                  </div>
                  {!lista.esPorDefecto && (
                    <button
                      type="button"
                      className="pequeno"
                      style={{ alignSelf: 'flex-end' }}
                      onClick={lista.limpiar}
                    >
                      Limpiar filtros
                    </button>
                  )}
                </FiltrosResponsive>
                {filtrados.length === 0 ? (
                  <div className="panel-cuerpo">
                    <p className="suave">No hay clientes que coincidan con los filtros.</p>
                  </div>
                ) : (
                  <div className="tabla-desliza">
                    <table>
                      <thead>
                        <tr>
                          <CabeceraOrdenable
                            texto="Cliente"
                            columna="razonSocial"
                            ordenActual={orden}
                            dirActual={dir}
                            onClick={() => ordenarPor('razonSocial')}
                          />
                          <CabeceraOrdenable
                            texto="Código"
                            columna="codigo"
                            ordenActual={orden}
                            dirActual={dir}
                            onClick={() => ordenarPor('codigo')}
                          />
                          <th className="num">Centros</th>
                          <CabeceraOrdenable
                            texto="Estado"
                            columna="estado"
                            ordenActual={orden}
                            dirActual={dir}
                            onClick={() => ordenarPor('estado')}
                          />
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {filtrados.map((c) => (
                          <tr
                            key={c.id}
                            className="pulsable"
                            onClick={() => navegar(`/clientes/${c.id}`)}
                          >
                            <td>
                              <span className="con-avatar">
                                <AvatarCliente cliente={c} pequeno />
                                {c.razonSocial}
                              </span>
                            </td>
                            <td className="cod">{c.codigo}</td>
                            <td className="num">{c.centros}</td>
                            <td>
                              <span className={`etiqueta ${c.activo ? 'exito' : 'neutra'}`}>
                                {c.activo ? 'ACTIVO' : 'INACTIVO'}
                              </span>
                            </td>
                            <td className="derecha">
                              <button className="pequeno">Abrir</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )
          }
        </Panel>
      </div>
    </Shell>
  );
}

type OrdenCentros = 'codigo' | 'descripcion' | 'estado';

/** Mismo criterio que Clientes: `codigo ASC` es el orden que ya devuelve hoy el backend. */
const DEFAULTS_CENTROS = { q: '', estado: 'TODOS', orden: 'codigo', dir: 'asc' } as const;

export function PaginaCliente(): ReactNode {
  const { idCliente = '' } = useParams();
  const { puede } = useSesion();
  const navegar = useNavigate();

  //  Una sola carga de `ClienteDetalle` para toda la ficha — cabecera,
  //  panel de lectura y formulario de edición comparten estos mismos
  //  datos (3.3-C2), en vez de que cada uno pida lo suyo por su cuenta.
  //  Desde 3.3-C2 `/detalle` exige `clientes:leer` (no `clientes:editar`):
  //  el gate anterior era un accidente de que sólo lo llamaba la edición,
  //  no una decisión de ocultar nif/email/teléfono/dirección a quien sólo
  //  puede leer.
  const cliente = useDatos(() => api.get<ClienteDetalle>(`/api/clientes/${idCliente}/detalle`), [idCliente]);
  const centros = useDatos(
    () => api.get<{ centros: Centro[] }>(`/api/clientes/${idCliente}/centros`),
    [idCliente],
  );
  const [mostrarEdicion, setMostrarEdicion] = useState(false);
  const [mostrarNuevoCentro, setMostrarNuevoCentro] = useState(false);
  const listaCentros = useListaUrl(DEFAULTS_CENTROS);

  const qCentro = listaCentros.valor('q');
  const filtroEstadoCentro = listaCentros.valor('estado') as EstadoFiltro;
  const ordenCentro = listaCentros.valor('orden') as OrdenCentros;
  const dirCentro = listaCentros.valor('dir') as Dir;

  function ordenarCentrosPor(columna: OrdenCentros): void {
    const siguiente = alternarOrden(columna, ordenCentro, dirCentro);
    listaCentros.fijarVarios({ orden: siguiente.orden, dir: siguiente.dir });
  }

  //  FASE 3.3-D3: badge «Filtros (N)» — nunca `q`/`orden`/`dir`.
  const contadorFiltrosCentro = filtroEstadoCentro !== DEFAULTS_CENTROS.estado ? 1 : 0;

  const centrosFiltrados = useMemo(() => {
    const t = qCentro.trim().toLowerCase();
    let l = centros.datos?.centros ?? [];
    if (filtroEstadoCentro === 'ACTIVOS') l = l.filter((c) => c.activo);
    else if (filtroEstadoCentro === 'INACTIVOS') l = l.filter((c) => !c.activo);
    if (t !== '') {
      l = l.filter((c) => c.descripcion.toLowerCase().includes(t) || c.codigo.toLowerCase().includes(t));
    }
    const factor = dirCentro === 'asc' ? 1 : -1;
    return [...l].sort((a, b) => {
      if (ordenCentro === 'descripcion') return factor * a.descripcion.localeCompare(b.descripcion);
      if (ordenCentro === 'estado') return factor * (Number(a.activo) - Number(b.activo));
      return factor * a.codigo.localeCompare(b.codigo);
    });
  }, [centros.datos, qCentro, filtroEstadoCentro, ordenCentro, dirCentro]);

  function abrirEdicion() {
    setMostrarEdicion(true);
    setMostrarNuevoCentro(false);
  }

  function abrirNuevoCentro() {
    setMostrarNuevoCentro(true);
    setMostrarEdicion(false);
  }

  return (
    <Shell
      migas={[{ texto: 'Clientes', a: '/clientes' }, { texto: cliente.datos?.razonSocial ?? '…' }]}
      titulo={
        <span className="fila" style={{ gap: 10 }}>
          {cliente.datos !== null && <Avatar nombre={cliente.datos.razonSocial} logoUrl={cliente.datos.logoUrl} />}
          <span>{cliente.datos?.razonSocial ?? 'Cliente'}</span>
          {cliente.datos !== null && (
            <span className={`etiqueta ${cliente.datos.activo ? 'exito' : 'neutra'}`}>
              {cliente.datos.activo ? 'ACTIVO' : 'INACTIVO'}
            </span>
          )}
        </span>
      }
      subtitulo={
        cliente.datos === null ? undefined : (
          <>
            <span className="mono">{cliente.datos.codigo}</span>
            {cliente.datos.nif.trim() !== '' && (
              <>
                <span aria-hidden="true" className="tenue">·</span>
                <span className="mono">{cliente.datos.nif}</span>
              </>
            )}
          </>
        )
      }
      acciones={
        puede('clientes:editar') ? (
          <button onClick={() => (mostrarEdicion ? setMostrarEdicion(false) : abrirEdicion())}>
            {mostrarEdicion ? 'Cancelar edición' : 'Editar cliente'}
          </button>
        ) : undefined
      }
    >
      <div className="panel">
        <div className="panel-cabecera">
          <h2>Datos del cliente</h2>
          {puede('clientes:editar') && cliente.datos !== null && (
            <GestorLogoCliente
              idCliente={idCliente}
              logoUrl={cliente.datos.logoUrl}
              alCambiar={() => cliente.recargar()}
            />
          )}
        </div>
        <Panel estado={cliente}>
          {(d) =>
            mostrarEdicion ? (
              <FormEditarClienteConDatos
                datos={d}
                idCliente={idCliente}
                alEditar={() => {
                  setMostrarEdicion(false);
                  cliente.recargar();
                  centros.recargar();
                }}
                alCancelar={() => setMostrarEdicion(false)}
              />
            ) : (
              <VistaCliente datos={d} />
            )
          }
        </Panel>
      </div>

      {puede('tipos_limpieza:leer') && (
        <div className="panel">
          <div className="panel-cabecera">
            <h2>Tipos de limpieza</h2>
            <button className="pequeno" onClick={() => navegar(`/clientes/${idCliente}/tipos-limpieza`)}>
              Ver tipos
            </button>
          </div>
        </div>
      )}

      {puede('portal:configurar') && (
        <div className="panel">
          <div className="panel-cabecera">
            <h2>Indicadores del portal</h2>
            <button className="pequeno" onClick={() => navegar(`/clientes/${idCliente}/portal-indicadores`)}>
              Configurar
            </button>
          </div>
        </div>
      )}

      <div className="panel">
        <div className="panel-cabecera">
          <h2>Centros</h2>
          {puede('centros:crear') && !mostrarNuevoCentro && (
            <button className="pequeno" onClick={abrirNuevoCentro}>
              Nuevo centro
            </button>
          )}
        </div>
        {mostrarNuevoCentro && (
          <FormNuevoCentro
            idCliente={idCliente}
            alCrear={(id) => {
              setMostrarNuevoCentro(false);
              centros.recargar();
              navegar(`/centros/${id}`);
            }}
            alCancelar={() => setMostrarNuevoCentro(false)}
          />
        )}
        <div className="panel-cuerpo sin-relleno">
          <Panel estado={centros}>
            {(d) =>
              d.centros.length === 0 ? (
                <Vacio titulo="Este cliente no tiene centros a tu alcance" />
              ) : (
                <>
                  <FiltrosResponsive
                    contador={contadorFiltrosCentro}
                    busqueda={
                      <div className="campo campo-busqueda">
                        <label htmlFor="buscar-centro-cliente">Buscar</label>
                        <input
                          id="buscar-centro-cliente"
                          type="text"
                          placeholder="Nombre o código…"
                          value={qCentro}
                          onChange={(e) => listaCentros.fijar('q', e.target.value)}
                        />
                      </div>
                    }
                  >
                    <div className="campo">
                      <label htmlFor="filtro-estado-centro">Estado</label>
                      <select
                        id="filtro-estado-centro"
                        value={filtroEstadoCentro}
                        onChange={(e) => listaCentros.fijar('estado', e.target.value)}
                      >
                        <option value="TODOS">Todos</option>
                        <option value="ACTIVOS">Activos</option>
                        <option value="INACTIVOS">Inactivos</option>
                      </select>
                    </div>
                    {!listaCentros.esPorDefecto && (
                      <button
                        type="button"
                        className="pequeno"
                        style={{ alignSelf: 'flex-end' }}
                        onClick={listaCentros.limpiar}
                      >
                        Limpiar filtros
                      </button>
                    )}
                  </FiltrosResponsive>
                  {centrosFiltrados.length === 0 ? (
                    <div className="panel-cuerpo">
                      <p className="suave">No hay centros que coincidan con los filtros.</p>
                    </div>
                  ) : (
                    <div className="tabla-desliza">
                      <table>
                        <thead>
                          <tr>
                            <CabeceraOrdenable
                              texto="Centro"
                              columna="descripcion"
                              ordenActual={ordenCentro}
                              dirActual={dirCentro}
                              onClick={() => ordenarCentrosPor('descripcion')}
                            />
                            <CabeceraOrdenable
                              texto="Código"
                              columna="codigo"
                              ordenActual={ordenCentro}
                              dirActual={dirCentro}
                              onClick={() => ordenarCentrosPor('codigo')}
                            />
                            <th>Días operativos</th>
                            <CabeceraOrdenable
                              texto="Estado"
                              columna="estado"
                              ordenActual={ordenCentro}
                              dirActual={dirCentro}
                              onClick={() => ordenarCentrosPor('estado')}
                            />
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {centrosFiltrados.map((c) => (
                            <tr
                              key={c.id}
                              className="pulsable"
                              onClick={() => navegar(`/centros/${c.id}`)}
                            >
                              <td>
                                <span className="con-avatar">
                                  {/* Mismo logo para toda la tabla — un solo cliente — reutilizando
                                      el que ya trajo la ficha (`cliente.datos.logoUrl`), sin pedirlo
                                      otra vez por fila: `c.clienteLogoUrl` es siempre `null` en este
                                      listado a propósito (ver CentroResumen en consultas.ts). */}
                                  <Avatar nombre={c.descripcion} logoUrl={cliente.datos?.logoUrl} pequeno />
                                  {c.descripcion}
                                </span>
                              </td>
                              <td className="cod">{c.codigo}</td>
                              <td className="suave">{etiquetaDias(c.diasOperativos)}</td>
                              <td>
                                <span className={`etiqueta ${c.activo ? 'exito' : 'neutra'}`}>
                                  {c.activo ? 'ACTIVO' : 'INACTIVO'}
                                </span>
                              </td>
                              <td className="derecha">
                                <button className="pequeno">Abrir</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )
            }
          </Panel>
        </div>
      </div>
    </Shell>
  );
}

/**
 * SUBIR / SUSTITUIR / QUITAR LOGOTIPO (L99 4.3-F, punto 4)
 *
 * Control independiente de «Editar cliente»: subir una imagen no es
 * editar los campos de texto, y obligar a entrar en modo edición sólo
 * para esto sería fricción sin motivo. Mismo permiso (`clientes:editar`)
 * que el resto de la edición — la ruta ya lo exige otra vez en el
 * servidor, esto es sólo para no ofrecer un control que va a fallar.
 */
const FORMATOS_LOGO_ACEPTADOS = 'image/png,image/jpeg,image/webp';

function GestorLogoCliente({
  idCliente,
  logoUrl,
  alCambiar,
}: {
  idCliente: string;
  logoUrl: string | null;
  alCambiar: () => void;
}): ReactNode {
  const inputRef = useRef<HTMLInputElement>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function subir(fichero: File): Promise<void> {
    setEnviando(true);
    setError(null);
    try {
      const datos = new FormData();
      datos.append('logo', fichero);
      await api.subir<ClienteDetalle>(`/api/clientes/${idCliente}/logo`, datos);
      alCambiar();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
      if (inputRef.current !== null) inputRef.current.value = '';
    }
  }

  async function quitar(): Promise<void> {
    setEnviando(true);
    setError(null);
    try {
      await api.del(`/api/clientes/${idCliente}/logo`);
      alCambiar();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="fila" style={{ gap: 8, alignItems: 'center' }}>
      <input
        ref={inputRef}
        type="file"
        accept={FORMATOS_LOGO_ACEPTADOS}
        hidden
        onChange={(ev) => {
          const fichero = ev.target.files?.[0];
          if (fichero !== undefined) void subir(fichero);
        }}
      />
      <button type="button" className="pequeno" disabled={enviando} onClick={() => inputRef.current?.click()}>
        {enviando ? 'Enviando…' : logoUrl === null ? 'Subir logo' : 'Cambiar logo'}
      </button>
      {logoUrl !== null && (
        <button type="button" className="pequeno" disabled={enviando} onClick={() => void quitar()}>
          Quitar logo
        </button>
      )}
      {error !== null && <ErrorCaja error={error} />}
    </div>
  );
}

/** Modo lectura de «Datos del cliente» — sin entrar en Editar cliente. */
function VistaCliente({ datos: d }: { datos: ClienteDetalle }): ReactNode {
  return (
    <div className="panel-cuerpo">
      <div className="datos">
        <div className="dato">
          <div className="etiqueta-dato">Código</div>
          <div className="valor-dato mono">{d.codigo}</div>
        </div>
        <div className="dato">
          <div className="etiqueta-dato">NIF</div>
          <div className="valor-dato mono">{d.nif}</div>
        </div>
        <div className="dato">
          <div className="etiqueta-dato">Email</div>
          <div className="valor-dato">{d.email ?? '—'}</div>
        </div>
        <div className="dato">
          <div className="etiqueta-dato">Teléfono</div>
          <div className="valor-dato">{d.telefono ?? '—'}</div>
        </div>
        <div className="dato">
          <div className="etiqueta-dato">Dirección</div>
          <div className="valor-dato">{d.direccion ?? '—'}</div>
        </div>
        <div className="dato">
          <div className="etiqueta-dato">Estado</div>
          <div className="valor-dato">
            <span className={`etiqueta ${d.activo ? 'exito' : 'neutra'}`}>
              {d.activo ? 'ACTIVO' : 'INACTIVO'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------- formularios

function FormNuevoCliente({
  alCrear,
  alCancelar,
}: {
  alCrear: (id: string) => void;
  alCancelar: () => void;
}): ReactNode {
  const [codigo, setCodigo] = useState('');
  const [razonSocial, setRazonSocial] = useState('');
  const [nif, setNif] = useState('');
  const [direccion, setDireccion] = useState('');
  const [email, setEmail] = useState('');
  const [telefono, setTelefono] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [camposMal, setCamposMal] = useState<Record<string, string>>({});

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    setCamposMal({});
    try {
      const r = await api.post<ClienteDetalle>('/api/clientes', {
        codigo,
        razonSocial,
        nif,
        direccion: direccion.trim() || null,
        email: email.trim() || null,
        telefono: telefono.trim() || null,
      });
      alCrear(r.id);
    } catch (err) {
      if (err instanceof FalloApi && err.erroresDeCampo != null) setCamposMal(err.erroresDeCampo);
      else setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="form-inline" onSubmit={(e) => { void enviar(e); }}>
      <div className="form-grupo">
        <CampoTexto label="Código" value={codigo} onChange={setCodigo} error={camposMal['codigo']} disabled={enviando} required />
        <CampoTexto label="Razón social" value={razonSocial} onChange={setRazonSocial} error={camposMal['razonSocial']} disabled={enviando} required />
        <CampoTexto label="NIF" value={nif} onChange={setNif} error={camposMal['nif']} disabled={enviando} required />
        <CampoTexto label="Dirección" value={direccion} onChange={setDireccion} error={camposMal['direccion']} disabled={enviando} />
        <CampoTexto label="Email" type="email" value={email} onChange={setEmail} error={camposMal['email']} disabled={enviando} />
        <CampoTexto label="Teléfono" value={telefono} onChange={setTelefono} error={camposMal['telefono']} disabled={enviando} />
      </div>
      {error !== null && <ErrorCaja error={error} />}
      <div className="fila-botones">
        <button type="submit" className="principal" disabled={enviando}>
          {enviando ? 'Guardando…' : 'Crear cliente'}
        </button>
        <button type="button" onClick={alCancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

function FormEditarClienteConDatos({
  datos,
  idCliente,
  alEditar,
  alCancelar,
}: {
  datos: ClienteDetalle;
  idCliente: string;
  alEditar: () => void;
  alCancelar: () => void;
}): ReactNode {
  const [razonSocial, setRazonSocial] = useState(datos.razonSocial);
  const [nif, setNif] = useState(datos.nif);
  const [direccion, setDireccion] = useState(datos.direccion ?? '');
  const [email, setEmail] = useState(datos.email ?? '');
  const [telefono, setTelefono] = useState(datos.telefono ?? '');
  const [activo, setActivo] = useState(datos.activo);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [camposMal, setCamposMal] = useState<Record<string, string>>({});

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    setCamposMal({});
    try {
      await api.patch<ClienteDetalle>(`/api/clientes/${idCliente}`, {
        razonSocial,
        nif,
        direccion: direccion.trim() || null,
        email: email.trim() || null,
        telefono: telefono.trim() || null,
        activo,
      });
      alEditar();
    } catch (err) {
      if (err instanceof FalloApi && err.erroresDeCampo != null) setCamposMal(err.erroresDeCampo);
      else setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="form-inline" onSubmit={(e) => { void enviar(e); }}>
      <div className="form-grupo">
        <div className="campo">
          <label>Código</label>
          <input type="text" value={datos.codigo} disabled />
        </div>
        <CampoTexto label="Razón social" value={razonSocial} onChange={setRazonSocial} error={camposMal['razonSocial']} disabled={enviando} required />
        <CampoTexto label="NIF" value={nif} onChange={setNif} error={camposMal['nif']} disabled={enviando} required />
        <CampoTexto label="Dirección" value={direccion} onChange={setDireccion} error={camposMal['direccion']} disabled={enviando} />
        <CampoTexto label="Email" type="email" value={email} onChange={setEmail} error={camposMal['email']} disabled={enviando} />
        <CampoTexto label="Teléfono" value={telefono} onChange={setTelefono} error={camposMal['telefono']} disabled={enviando} />
      </div>
      <div className="campo" style={{ marginTop: 4 }}>
        <label style={{ textTransform: 'none', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: 'var(--texto)', fontWeight: 'normal', marginBottom: 0 }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={activo}
            onChange={(e) => setActivo(e.target.checked)}
            disabled={enviando}
          />
          Cliente activo
        </label>
      </div>
      {error !== null && <ErrorCaja error={error} />}
      <div className="fila-botones">
        <button type="submit" className="principal" disabled={enviando}>
          {enviando ? 'Guardando…' : 'Guardar cambios'}
        </button>
        <button type="button" onClick={alCancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

function FormNuevoCentro({
  idCliente,
  alCrear,
  alCancelar,
}: {
  idCliente: string;
  alCrear: (id: string) => void;
  alCancelar: () => void;
}): ReactNode {
  const [codigo, setCodigo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [direccion, setDireccion] = useState('');
  const [diasOperativos, setDiasOperativos] = useState<number[] | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [camposMal, setCamposMal] = useState<Record<string, string>>({});

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    setCamposMal({});
    try {
      const r = await api.post<CentroDetalle>(`/api/clientes/${idCliente}/centros`, {
        codigo,
        descripcion,
        direccion: direccion.trim() || null,
        diasOperativos,
      });
      alCrear(r.id);
    } catch (err) {
      if (err instanceof FalloApi && err.erroresDeCampo != null) setCamposMal(err.erroresDeCampo);
      else setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="form-inline" onSubmit={(e) => { void enviar(e); }}>
      <div className="form-grupo">
        <CampoTexto label="Código" value={codigo} onChange={setCodigo} error={camposMal['codigo']} disabled={enviando} required />
        <CampoTexto label="Descripción" value={descripcion} onChange={setDescripcion} error={camposMal['descripcion']} disabled={enviando} required />
        <CampoTexto label="Dirección" value={direccion} onChange={setDireccion} error={camposMal['direccion']} disabled={enviando} />
      </div>
      <div className="campo" style={{ marginTop: 4 }}>
        <label>Días operativos</label>
        <DiasOperativosInput value={diasOperativos} onChange={setDiasOperativos} disabled={enviando} />
      </div>
      {error !== null && <ErrorCaja error={error} />}
      <div className="fila-botones">
        <button type="submit" className="principal" disabled={enviando}>
          {enviando ? 'Guardando…' : 'Crear centro'}
        </button>
        <button type="button" onClick={alCancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

// --------------------------------------------------------------- utilidades

const ABREV_DIA = ['', 'L', 'M', 'X', 'J', 'V', 'S', 'D'];

function etiquetaDias(dias: number[] | null): string {
  if (dias === null) return 'Todos';
  if (dias.length === 0) return '—';
  return dias
    .slice()
    .sort((a, b) => a - b)
    .map((d) => ABREV_DIA[d] ?? String(d))
    .join(' ');
}
